import * as React from 'react'
import { Navigate } from 'react-router-dom'

import {
  ApprovalGroupRootPageDocument,
  ApprovalsValueObjectsApprovalCategoryEnum,
  GetRulesConfigurationDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { ApprovalGroupDetailHeader } from './ApprovalGroupDetailHeader'
import { settings } from './settings'

const RootPage = React.lazy(() => import('modules/approvals/pages/settings/RootPage'))
const ApprovalGroupPage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/ApprovalGroupPage')
)
const ApprovalRulePage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/ApprovalRulePage')
)
const CreateApprovalRulePage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/CreateApprovalRulePage')
)
const DetailsPage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/DetailsPage')
)
const EmployeesPage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/EmployeesPage')
)
const UpdateApprovalRulePage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/UpdateApprovalRulePage')
)
const EditGroupPage = React.lazy(
  () => import('modules/approvals/pages/settings/RootPage/EditGroupPage')
)

export const approvalsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.approvals.path,
      children: [
        {
          path: navigation.approvals.approvalGroupsCategoryId.path,
          element: <RootPage />,
          children: [
            {
              index: true,
              element: (
                <Navigate to={navigation.approvals.approvalGroupsCategoryId.details.path} replace />
              ),
            },
            {
              path: navigation.approvals.approvalGroupsCategoryId.details.path,
              element: <ApprovalGroupPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: ApprovalGroupRootPageDocument,
                    variables: {
                      id: String(params.id),
                      companyId: session.company.id,
                      // eslint-disable-next-line no-type-assertion/no-type-assertion
                      category: params.category as ApprovalsValueObjectsApprovalCategoryEnum,
                    },
                  }).map(({ data }) => {
                    const { href, configurationHref, employeesHref } = settings(data, navigation)

                    if (!data.approvals.approvalGroup) return
                    return {
                      title: data.approvals.approvalGroup.name,
                      headerComponent: (
                        <ApprovalGroupDetailHeader
                          ctaLink={href}
                          approvalGroup={data.approvals.approvalGroup}
                        />
                      ),
                      subNavigation: [
                        {
                          title: i18n.t('settings.approvals.tabs.approval_rules'),
                          href: configurationHref,
                          index: true,
                        },
                        {
                          title: i18n.t('settings.approvals.tabs.employees'),
                          href: employeesHref,
                        },
                      ],
                    }
                  })
                },
              },
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      to={navigation.approvals.approvalGroupsCategoryId.details.configuration.path}
                      replace
                    />
                  ),
                },
                {
                  path: navigation.approvals.approvalGroupsCategoryId.details.configuration.path,
                  element: <DetailsPage />,
                  children: [
                    {
                      path: navigation.approvals.approvalGroupsCategoryId.details.configuration
                        .editGroup.path,
                      element: <EditGroupPage />,
                    },
                  ],
                },
                {
                  path: navigation.approvals.approvalGroupsCategoryId.details.employees.path,
                  handle: {
                    meta: () => ({
                      title: i18n.t('settings.approvals.tabs.employees'),
                    }),
                  },
                  element: <EmployeesPage />,
                  children: [
                    {
                      path: navigation.approvals.approvalGroupsCategoryId.details.employees
                        .editGroup.path,
                      element: <EditGroupPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: navigation.approvals.approvalGroupsCategoryId.rulesConfigurations.path,
              children: [
                {
                  path: navigation.approvals.approvalGroupsCategoryId.rulesConfigurations.new.path,
                  handle: {
                    meta: () => ({
                      title: i18n.t('settings.approvals.approval_rule.add_rule'),
                    }),
                  },
                  element: <CreateApprovalRulePage />,
                },
                {
                  path: navigation.approvals.approvalGroupsCategoryId.rulesConfigurations.edit.path,
                  handle: {
                    meta: ({ params, watchQuery, session }) =>
                      watchQuery({
                        query: GetRulesConfigurationDocument,
                        variables: {
                          companyId: session.company.id,
                          id: String(params['rules_configuration_id']),
                          // eslint-disable-next-line no-type-assertion/no-type-assertion
                          category: params.category as ApprovalsValueObjectsApprovalCategoryEnum,
                        },
                      }).map(({ data }) => {
                        const ruleConfiguration = data?.approvals.rulesConfiguration
                        const title = ruleConfiguration ? ruleConfiguration.name : ''
                        return {
                          title,
                        }
                      }),
                  },
                  element: <UpdateApprovalRulePage />,
                },
              ],
              element: <ApprovalRulePage />,
            },
          ],
        },
      ],
    },
  ]
}
